import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChefHat, Heart, MapPin, Star, ArrowLeft, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/lib/auth-context';
import api from '@/lib/api';
import { toast } from 'sonner';

const SavedRestaurantsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const langCode = (i18n.language || 'tr').split('-')[0];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSaved = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      const res = await api.get('/saved-restaurants');
      setItems(res.data.items || []);
    } catch (e) {
      console.error(e);
      toast.error(t('restaurantsPage.savedLoadError', 'Kayıtlı restoranlar yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [user, t]);

  useEffect(() => {
    if (!user) {
      navigate(`/${langCode}/login?redirect=/${langCode}/account/saved-restaurants`);
      return;
    }
    fetchSaved();
  }, [user, langCode, navigate, fetchSaved]);

  const handleRemove = async (restaurantId, name) => {
    try {
      await api.post('/saved-restaurants/toggle', { restaurant_id: restaurantId });
      setItems((prev) => prev.filter((i) => i.restaurant_id !== restaurantId));
      toast.success(t('restaurantsPage.removedFromSaved', { name: name || '' }));
    } catch (e) {
      toast.error(t('restaurantsPage.saveError', 'İşlem başarısız'));
    }
  };

  return (
    <div className="py-8 min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 md:px-8 max-w-4xl">
        <Link
          to={`/${langCode}/restaurants`}
          className="inline-flex items-center text-sm text-muted-foreground hover:text-red-600 mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('restaurantsPage.backToRestaurants')}
        </Link>

        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <Heart className="w-6 h-6 text-red-600 fill-red-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">
              {t('restaurantsPage.savedRestaurantsTitle', 'Kaydettiğim Restoranlar')}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t('restaurantsPage.savedRestaurantsDesc', 'Favori Türk restoranlarınız burada listelenir')}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-red-600" />
          </div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-lg font-medium mb-2">
                {t('restaurantsPage.savedEmptyTitle', 'Henüz kayıtlı restoran yok')}
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                {t('restaurantsPage.savedEmptyDesc', 'Restoran sayfasındaki Kaydet butonu ile favorilerinize ekleyin.')}
              </p>
              <Link to={`/${langCode}/restaurants`}>
                <Button className="bg-red-600 hover:bg-red-700">
                  {t('restaurantsPage.exploreRestaurants', 'Restoranları Keşfet')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map(({ restaurant_id, restaurant }) => (
              <Card key={restaurant_id} className="overflow-hidden hover:shadow-md transition-shadow">
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row">
                    <div className="sm:w-40 h-32 sm:h-auto bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center shrink-0">
                      {restaurant.images?.[0] ? (
                        <img
                          src={restaurant.images[0]}
                          alt={restaurant.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <ChefHat className="w-12 h-12 text-red-300" />
                      )}
                    </div>
                    <div className="flex-1 p-4 flex flex-col justify-between">
                      <div>
                        <Link
                          to={`/${langCode}/restaurants/${restaurant_id}`}
                          className="font-semibold text-lg hover:text-red-600"
                        >
                          {restaurant.name}
                        </Link>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{restaurant.city}, {restaurant.country}</span>
                          <span className="flex items-center gap-0.5">
                            <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                            {restaurant.rating?.toFixed(1) || '4.5'}
                          </span>
                          {restaurant.cuisine_type && (
                            <Badge variant="secondary">{restaurant.cuisine_type}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Link to={`/${langCode}/restaurants/${restaurant_id}`}>
                          <Button size="sm" variant="outline">
                            {t('restaurantsPage.viewRestaurant', 'Görüntüle')}
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => handleRemove(restaurant_id, restaurant.name)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {t('common.remove', 'Kaldır')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SavedRestaurantsPage;
